import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useCachedState } from "@/lib/swr";
import { fileUrl } from "./ctx";
import { toast } from "sonner";
import { usePaged, ShowMore } from "@/components/Paged";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import {
  Folder, FolderPlus, FolderOpen, FileText, Search, ArrowUpDown, MoreVertical, Download, Eye,
  Trash2, Share2, FolderInput, Pencil, Link2, Copy, X, Check, Loader2, Files, AlertCircle, RotateCcw, Lock, Users, ShieldCheck,
  FilePlus, GripVertical, LayoutGrid, List, Globe, ChevronRight, FolderDown, Tag, PanelLeftClose, PanelLeftOpen,
  Archive, ArrowRightLeft,
  Link2Off,
} from "lucide-react";
import { ExpiryBadge } from "@/components/knowledge/Expiry";
import { useAudiences, chipsOf, isOpen, describeChips } from "@/lib/audiences";
import { V } from "@/lib/vocab";

const TYPE_OF = (d) => {
  const f = (d.filename || "").toLowerCase();
  if (f.endsWith(".pdf")) return "PDF";
  if (f.endsWith(".doc") || f.endsWith(".docx")) return "Word";
  if (f.endsWith(".xls") || f.endsWith(".xlsx") || f.endsWith(".csv")) return "Spreadsheet";
  if (f.endsWith(".ppt") || f.endsWith(".pptx")) return "Slides";
  if (f.endsWith(".txt") || f.endsWith(".md")) return "Text";
  if (/\.(png|jpe?g|webp|gif|bmp|tiff?|heic)$/.test(f)) return "Image";
  if (!d.storage_path) return "Note";
  return d.doc_type || "Document";
};

// Visible "Text as link" switch on every stored file — the same setting as the "…" menu item.
// Off = the AI still answers from the file but never texts a download link for it.
const LinkShareCheck = ({ d, readOnly, onToggle }) => {
  if (!d.storage_path) return null;
  const on = d.share_by_link !== false;
  return (
    <label data-testid={`file-text-link-${d.id}`} onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold whitespace-nowrap select-none ${readOnly ? "text-ink/45" : "text-ink/70 cursor-pointer hover:text-ink"}`}
      title={on ? "Residents can be texted a download link for this file" : "The AI answers from this file but never texts a download link"}>
      <input type="checkbox" data-testid={`file-text-link-check-${d.id}`} checked={on} disabled={readOnly}
        onChange={() => onToggle(d, !on)} className="h-3.5 w-3.5 rounded border-sand-300 accent-redline-500" />
      Text as link
    </label>
  );
};

// Compact audience/verify indicators next to the filename — used to be squeezed into the
// Type column beside the file-type pill, which made a two-word file type look messy with
// a wrapping "Residents & board" chip. Now they sit right beside the title, self-labeled
// with an icon, and only appear when non-default (audience !== anyone or ID-check on).
const AudienceBadge = ({ d, compact = false, labels = {} }) => {
  const chips = chipsOf(d);
  const open = isOpen(chips);
  const rv = !!d.require_verification;
  const noLink = d.share_by_link === false;
  if (open && !rv && !noLink) return null;
  const badges = [];
  if (noLink) {
    badges.push(
      <span key="l" data-testid={`doc-nolink-badge-${d.id}`}
        className="inline-flex items-center gap-1 rounded-full bg-sand-100 text-ink/70 border border-sand-200 px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap"
        title="The AI can answer from this file but will never text a download link for it">
        <Link2Off className="h-3 w-3" strokeWidth={2} /> No link sharing
      </span>
    );
  }
  if (!open) {
    badges.push(
      <span key="a" data-testid={`doc-audience-badge-${d.id}`}
        className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap"
        title={`Shared only with: ${describeChips(chips, labels)}`}>
        <Lock className="h-3 w-3" strokeWidth={2} /> {describeChips(chips, labels)}
      </span>
    );
  }
  if (rv) {
    badges.push(
      <span key="v" data-testid={`doc-verify-badge-${d.id}`}
        className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap"
        title="The AI verifies the person's identity with a one-time code before sharing this">
        <ShieldCheck className="h-3 w-3" strokeWidth={2} /> ID check
      </span>
    );
  }
  return <span className={`inline-flex items-center gap-1.5 flex-wrap ${compact ? "" : "mt-1"}`}>{badges}</span>;
};

function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 86400000;
    if (diff < 1) return "Today";
    if (diff < 2) return "Yesterday";
    if (diff < 7) return `${Math.floor(diff)} days ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

function within(iso, range) {
  if (range === "any" || !iso) return range === "any";
  const t = new Date(iso).getTime();
  const now = new Date();
  if (range === "7d") return t >= now.getTime() - 7 * 86400000;
  if (range === "30d") return t >= now.getTime() - 30 * 86400000;
  if (range === "year") return new Date(iso).getFullYear() === now.getFullYear();
  if (range === "lastyear") return new Date(iso).getFullYear() === now.getFullYear() - 1;
  return true;
}

const EMPTY_FOLDER_FORM = { name: "", parent_id: "", is_public: false, visibility: "residents" };

// Module-level (stable identity) inline tag editor: hover ✕ to remove, "+ tag" to add with
// Enter — no dialog. Tags feed the concierge's alias-boosted retrieval directly.
function TagChips({ d, readOnly, onSave, max = 4 }) {
  const [adding, setAdding] = useState(false);
  const [val, setVal] = useState("");
  const tags = d.tags || [];
  const commit = () => {
    const t = val.trim();
    setVal(""); setAdding(false);
    if (!t || tags.some((x) => x.toLowerCase() === t.toLowerCase()) || tags.length >= 10) return;
    onSave(d, [...tags, t]);
  };
  if (!tags.length && readOnly) return null;
  return (
    <span data-testid={`file-tag-chips-${d.id}`} className="flex items-center gap-1 mt-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
      {tags.slice(0, max).map((t) => (
        <span key={t} className="group/tag inline-flex items-center gap-1 shrink-0 whitespace-nowrap max-w-[150px] text-[10px] font-semibold text-sky-700 bg-sky-50 border border-sky-100 rounded-full pl-1.5 pr-1 py-px">
          <Tag className="h-2.5 w-2.5 shrink-0" /><span className="truncate">{t}</span>
          {!readOnly && (
            <span role="button" tabIndex={0} data-testid={`tag-remove-${d.id}-${t}`} title="Remove tag"
              onClick={(e) => { e.stopPropagation(); onSave(d, tags.filter((x) => x !== t)); }}
              className="opacity-0 group-hover/tag:opacity-100 text-sky-400 hover:text-redline-600 cursor-pointer transition-opacity leading-none px-0.5">×</span>
          )}
        </span>
      ))}
      {tags.length > max && <span className="text-[10px] text-muted-foreground">+{tags.length - max}</span>}
      {!readOnly && (adding ? (
        <input autoFocus data-testid={`tag-add-input-${d.id}`} value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setAdding(false); setVal(""); } }}
          onBlur={commit} placeholder="add tag…" onClick={(e) => e.stopPropagation()}
          className="w-24 text-[10px] rounded-full border border-sky-200 px-2 py-px outline-none focus:ring-1 focus:ring-sky-300" />
      ) : (
        <span role="button" tabIndex={0} data-testid={`tag-add-${d.id}`} title={`Add a name ${V.lcs("person")} use — the concierge will match it`}
          onClick={(e) => { e.stopPropagation(); setAdding(true); }}
          className="inline-flex items-center text-[10px] font-semibold text-sky-500/70 hover:text-sky-700 border border-dashed border-sky-200 hover:border-sky-300 rounded-full px-1.5 py-px cursor-pointer transition-colors">+ tag</span>
      ))}
    </span>
  );
}

export default function DocumentsLibrary({ tenantId, readOnly, docs, onReloadDocs, onPreview, uploadFiles, removeDoc, onArchive, onMove }) {
  const { list: audienceList, labels: audienceLabels } = useAudiences(tenantId);
  const [folders, setFolders] = useCachedState(`folders:${tenantId}`, []);
  const [unfiled, setUnfiled] = useCachedState(`unfiled:${tenantId}`, { doc_count: 0 });
  const [active, setActive] = useState("all"); // all | unfiled | <folderId>
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [modified, setModified] = useState("any");
  const [sortBy, setSortBy] = useState("recent");
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [foldersOpen, setFoldersOpen] = useState(() => {
    try { return localStorage.getItem("docs_folders_open") !== "0"; } catch { return true; }
  });
  const toggleFolders = () => setFoldersOpen((o) => {
    const n = !o; try { localStorage.setItem("docs_folders_open", n ? "1" : "0"); } catch {} return n;
  });
  const [folderForm, setFolderForm] = useState(EMPTY_FOLDER_FORM);
  const [renameFor, setRenameFor] = useState(null);
  const [folderName, setFolderName] = useState("");
  const [renameDocFor, setRenameDocFor] = useState(null);
  const [docTitle, setDocTitle] = useState("");
  const [tagsFor, setTagsFor] = useState(null);       // doc whose aliases are being edited
  const [tagsDraft, setTagsDraft] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [shareTarget, setShareTarget] = useState(null); // {kind, id, label}
  const [dragId, setDragId] = useState(null);       // doc being dragged
  const [dropTarget, setDropTarget] = useState(null); // folder id / "unfiled" being hovered
  const [folderQuery, setFolderQuery] = useState("");
  const [collapsed, setCollapsed] = useState({});   // folderId -> true when its subfolders are hidden
  const [zipBusy, setZipBusy] = useState(false);
  const [view, setView] = useState("list"); // list | grid
  const fileRef = useRef(null);

  const loadFolders = async () => {
    try {
      const r = await api.get(`/tenants/${tenantId}/folders`);
      setFolders(r.data.folders || []);
      setUnfiled(r.data.unfiled || { doc_count: 0 });
    } catch { /* ignore */ }
  };
  useEffect(() => { if (tenantId) loadFolders(); }, [tenantId, docs]); // eslint-disable-line react-hooks/exhaustive-deps

  const folderName_ = (id) => folders.find((f) => f.id === id)?.name || "";

  // ---- Folder tree (nested via parent_id) ----
  const childrenOf = useMemo(() => {
    const ids = new Set(folders.map((f) => f.id));
    const map = {};
    folders.forEach((f) => {
      const p = f.parent_id && ids.has(f.parent_id) ? f.parent_id : null;
      (map[p] = map[p] || []).push(f);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.name.localeCompare(b.name)));
    return map;
  }, [folders]);

  const flatFolders = useMemo(() => {
    const out = [];
    const walk = (pid, depth) => (childrenOf[pid] || []).forEach((f) => { out.push({ ...f, depth }); walk(f.id, depth + 1); });
    walk(null, 0);
    return out;
  }, [childrenOf]);

  const activeFolderId = active !== "all" && active !== "unfiled" ? active : "";

  const scoped = useMemo(() => {
    let list = docs || [];
    if (active === "unfiled") list = list.filter((d) => !d.folder_id);
    else if (active !== "all") list = list.filter((d) => d.folder_id === active);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((d) => `${d.title || ""} ${d.description || ""} ${d.filename || ""} ${(d.tags || []).join(" ")}`.toLowerCase().includes(q));
    if (typeFilter !== "all") list = list.filter((d) => TYPE_OF(d) === typeFilter);
    if (modified !== "any") list = list.filter((d) => within(d.updated_at || d.created_at, modified));
    const when = (d) => d.updated_at || d.created_at || "";
    list = [...list].sort((a, b) => {
      if (sortBy === "name") return (a.title || "").localeCompare(b.title || "");
      if (sortBy === "type") return TYPE_OF(a).localeCompare(TYPE_OF(b));
      return when(b).localeCompare(when(a));
    });
    return list;
  }, [docs, active, query, typeFilter, modified, sortBy]);

  const availableTypes = useMemo(() => {
    const s = new Set((docs || []).map(TYPE_OF));
    return Array.from(s).sort();
  }, [docs]);

  // Client-side "Show more" paging — keeps long libraries scannable. Resets to first page
  // whenever the folder, search, filters, or sort change so the visible slice always reflects
  // the current selection instead of stale earlier results.
  const pagerResetKey = `${active}|${query}|${typeFilter}|${modified}|${sortBy}|${view}`;
  const { visible: pagedDocs, remaining: pagedRemaining, showMore: showMoreDocs } =
    usePaged(scoped, { step: 24, resetKey: pagerResetKey });

  const openNewFolder = (parentId = "") => {
    setFolderForm({ ...EMPTY_FOLDER_FORM, parent_id: parentId || "" });
    setNewFolderOpen(true);
  };

  const createFolder = async () => {
    const name = folderForm.name.trim();
    if (!name) return;
    try {
      const r = await api.post(`/tenants/${tenantId}/folders`, {
        name, parent_id: folderForm.parent_id || "", is_public: !!folderForm.is_public,
        visibility: folderForm.visibility || "residents",
      });
      toast.success(`Folder "${name}" created${folderForm.is_public ? ` — it shows on your ${V.lc("org")} website` : ""}`);
      setNewFolderOpen(false); setFolderForm(EMPTY_FOLDER_FORM);
      loadFolders();
      if (r.data?.id) setActive(r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create folder"); }
  };

  const doRename = async () => {
    const name = folderName.trim();
    if (!name || !renameFor) return;
    try {
      await api.patch(`/tenants/${tenantId}/folders/${renameFor.id}`, { name });
      toast.success("Folder renamed");
      setRenameFor(null); setFolderName("");
      loadFolders();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not rename"); }
  };

  const doRenameDoc = async () => {
    const title = docTitle.trim();
    if (!title || !renameDocFor) return;
    try {
      await api.patch(`/tenants/${tenantId}/documents/${renameDocFor.id}`, { title });
      toast.success("File renamed");
      setRenameDocFor(null); setDocTitle("");
      onReloadDocs?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not rename file"); }
  };

  // ---- Aliases / tags — the names residents actually use ("CC&Rs" on "Cascade Bylaws").
  // The AI matches these when finding & sending documents and sees them in its context.
  const addTag = (raw) => {
    const t = (raw ?? tagInput).trim().replace(/,+$/, "").slice(0, 40);
    if (!t) return;
    setTagsDraft((cur) => (cur.some((x) => x.toLowerCase() === t.toLowerCase()) || cur.length >= 10 ? cur : [...cur, t]));
    setTagInput("");
  };
  const doSaveTags = async () => {
    if (!tagsFor) return;
    const tags = tagInput.trim() ? [...tagsDraft, tagInput.trim()].slice(0, 10) : tagsDraft;
    try {
      await api.patch(`/tenants/${tenantId}/documents/${tagsFor.id}`, { tags });
      toast.success(tags.length ? "Aliases saved — the AI now matches these names" : "Aliases cleared");
      setTagsFor(null); setTagsDraft([]); setTagInput("");
      onReloadDocs?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save aliases"); }
  };
  const saveTagsInline = async (d, tags) => {
    try {
      await api.patch(`/tenants/${tenantId}/documents/${d.id}`, { tags: tags.slice(0, 10) });
      onReloadDocs?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update tags"); }
  };

  const toggleVisibility = async (f) => {
    const next = f.visibility === "board" ? "residents" : "board";
    try {
      await api.patch(`/tenants/${tenantId}/folders/${f.id}`, { visibility: next });
      toast.success(next === "board" ? `"${f.name}" is now ${V.grp()} & managers only` : `"${f.name}" is now visible to ${V.lcs("person")}`);
      loadFolders();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change folder visibility"); }
  };

  const togglePublic = async (f) => {
    try {
      await api.patch(`/tenants/${tenantId}/folders/${f.id}`, { is_public: !f.is_public });
      toast.success(!f.is_public
        ? `"${f.name}" now shows on your ${V.lc("org")} website`
        : `"${f.name}" removed from the ${V.lc("org")} website`);
      loadFolders();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };

  const downloadFolderZip = async (f) => {
    setZipBusy(true);
    try {
      const r = await api.get(`/tenants/${tenantId}/folders/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${f.name}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`"${f.name}" exported as ZIP`);
    } catch (e) {
      let msg = "Could not export folder";
      try {
        if (e.response?.data instanceof Blob) msg = JSON.parse(await e.response.data.text()).detail || msg;
        else msg = e.response?.data?.detail || msg;
      } catch { /* keep default */ }
      toast.error(msg);
    } finally { setZipBusy(false); }
  };

  const deleteFolder = async (f) => {
    if (!window.confirm(`Delete folder "${f.name}"? Its documents move to Unfiled and subfolders move up a level (nothing is deleted).`)) return;
    try {
      await api.delete(`/tenants/${tenantId}/folders/${f.id}`);
      toast.success("Folder deleted");
      if (active === f.id) setActive("all");
      loadFolders(); onReloadDocs?.();
    } catch (e) { toast.error("Could not delete folder"); }
  };

  const moveDoc = async (doc, folderId) => {
    try {
      await api.patch(`/tenants/${tenantId}/documents/${doc.id}`, { folder_id: folderId || "" });
      toast.success(folderId ? `Moved to ${folderName_(folderId)}` : "Moved to Unfiled");
      onReloadDocs?.(); loadFolders();
    } catch (e) { toast.error("Could not move document"); }
  };

  const setDocAudiences = async (doc, audiences) => {
    try {
      await api.patch(`/tenants/${tenantId}/documents/${doc.id}`, { audiences });
      toast.success(`"${doc.title}" — AI shares this with: ${describeChips(audiences, audienceLabels)}`);
      onReloadDocs?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update audience"); }
  };
  const toggleDocChip = (doc, key) => {
    const cur = chipsOf(doc);
    if (key === "anyone") return setDocAudiences(doc, ["anyone"]);
    if (doc.require_verification && key === "anyone") return;
    const rest = cur.filter((k) => k !== "anyone");
    const next = rest.includes(key) ? rest.filter((k) => k !== key) : [...rest, key];
    return setDocAudiences(doc, next.length ? next : ["anyone"]);
  };

  const setDocShareByLink = async (doc, on) => {
    try {
      await api.patch(`/tenants/${tenantId}/documents/${doc.id}`, { share_by_link: on });
      toast.success(on ? `"${doc.title}" — the AI and your team can text a download link again`
        : `"${doc.title}" — link sharing off. The AI can still answer from it, but won't text the file.`);
      onReloadDocs?.();
    } catch (e) { toast.error("Could not update link sharing"); }
  };

  const setDocRequireVerify = async (doc, on) => {
    try {
      await api.patch(`/tenants/${tenantId}/documents/${doc.id}`, { require_verification: on });
      toast.success(on ? `"${doc.title}" — identity check now required before the AI shares this`
        : `"${doc.title}" — identity check removed`);
      onReloadDocs?.();
    } catch (e) { toast.error("Could not update identity check"); }
  };

  const reprocess = async (doc) => {
    try {
      await api.post(`/tenants/${tenantId}/documents/${doc.id}/reprocess`);
      toast.success("Retrying — reading & indexing again");
      onReloadDocs?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not retry"); }
  };

  const [retryingAll, setRetryingAll] = useState(false);
  const failedDocs = useMemo(() => (docs || []).filter((d) => d.status === "failed" && d.storage_path), [docs]);
  const reprocessAll = async () => {
    setRetryingAll(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/documents/reprocess-all`);
      const n = r.data?.retried || 0;
      toast[n ? "success" : "info"](n ? `Retrying ${n} interrupted document${n === 1 ? "" : "s"}…` : "Nothing to retry");
      onReloadDocs?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not retry"); }
    finally { setRetryingAll(false); }
  };

  // Keep the list live while anything is still indexing (survives a page refresh, unlike
  // the upload-session queue) so spinners resolve to Ready/Failed on their own.
  useEffect(() => {
    if (!(docs || []).some((d) => d.status === "processing" || d.status === "queued")) return;
    const iv = setInterval(() => onReloadDocs?.(), 3500);
    return () => clearInterval(iv);
  }, [docs, onReloadDocs]);

  const activeFolderObj = activeFolderId ? folders.find((f) => f.id === active) : null;
  // A folder nested inside a restricted one is restricted too (the engine enforces this), so the
  // New-folder dialog must not offer choices that the parent has already decided.
  const parentIsLocked = !!folders.find((f) => f.id === folderForm.parent_id)?.board_only;

  const renderMenu = (d) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={`file-menu-${d.id}`} className="h-8 w-8 rounded-full flex items-center justify-center text-ink/40 hover:text-ink hover:bg-sand-100 transition-colors"><MoreVertical className="h-4 w-4" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem data-testid={`file-preview-${d.id}`} onClick={() => onPreview?.(d)}><Eye className="h-4 w-4 mr-2" /> Preview</DropdownMenuItem>
        {!readOnly && <DropdownMenuItem data-testid={`file-rename-${d.id}`} onClick={() => { setRenameDocFor(d); setDocTitle(d.title || ""); }}><Pencil className="h-4 w-4 mr-2" /> Rename</DropdownMenuItem>}
        {!readOnly && <DropdownMenuItem data-testid={`file-tags-${d.id}`} onClick={() => { setTagsFor(d); setTagsDraft(d.tags || []); setTagInput(""); }}><Tag className="h-4 w-4 mr-2" /> Aliases &amp; tags</DropdownMenuItem>}
        {!readOnly && (d.status === "failed" || d.status === "stored") && <DropdownMenuItem data-testid={`file-retry-${d.id}`} onClick={() => reprocess(d)}><RotateCcw className="h-4 w-4 mr-2" /> Retry indexing</DropdownMenuItem>}
        {d.storage_path && (
          <DropdownMenuItem asChild>
            <a data-testid={`file-download-${d.id}`} href={fileUrl(d.storage_path)} download={d.filename || true}><Download className="h-4 w-4 mr-2" /> Download</a>
          </DropdownMenuItem>
        )}
        {!readOnly && <DropdownMenuItem data-testid={`file-share-${d.id}`} onClick={() => setShareTarget({ kind: "file", id: d.id, label: d.title })}><Share2 className="h-4 w-4 mr-2" /> Share link</DropdownMenuItem>}
        {!readOnly && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">AI shares with</DropdownMenuLabel>
            <DropdownMenuItem data-testid={`file-audience-anyone-${d.id}`} disabled={isOpen(chipsOf(d))} onClick={() => toggleDocChip(d, "anyone")}>
              {isOpen(chipsOf(d)) ? <Check className="h-4 w-4 mr-2 text-redline-500" /> : <Globe className="h-4 w-4 mr-2" />} Anyone
            </DropdownMenuItem>
            {audienceList.map((a) => {
              const on = !isOpen(chipsOf(d)) && chipsOf(d).includes(a.key);
              return (
                <DropdownMenuItem key={a.key} data-testid={`file-audience-${a.key}-${d.id}`} onClick={(e) => { e.preventDefault(); toggleDocChip(d, a.key); }} title={a.description || a.label}>
                  {on ? <Check className="h-4 w-4 mr-2 text-redline-500" /> : (a.group === "team" ? <Lock className="h-4 w-4 mr-2" /> : <Users className="h-4 w-4 mr-2" />)}
                  {a.label}
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuItem data-testid={`file-require-verify-${d.id}`} onClick={() => setDocRequireVerify(d, !d.require_verification)}>
              {d.require_verification ? <Check className="h-4 w-4 mr-2 text-amber-600" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
              {d.require_verification ? "Identity check required" : "Require identity check"}
            </DropdownMenuItem>
            {d.storage_path && (
              <DropdownMenuItem data-testid={`file-share-link-toggle-${d.id}`} onClick={() => setDocShareByLink(d, d.share_by_link === false)}
                title="Off: the AI can still answer from this file, but nobody gets a download link by text">
                {d.share_by_link === false ? <Link2Off className="h-4 w-4 mr-2 text-ink/60" /> : <Check className="h-4 w-4 mr-2 text-redline-500" />}
                {d.share_by_link === false ? "Link sharing off — turn on" : "Can be texted as a link"}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Move to</DropdownMenuLabel>
            <DropdownMenuItem data-testid={`file-move-unfiled-${d.id}`} disabled={!d.folder_id} onClick={() => moveDoc(d, "")}><FolderInput className="h-4 w-4 mr-2" /> Unfiled</DropdownMenuItem>
            {flatFolders.map((f) => (
              <DropdownMenuItem key={f.id} disabled={d.folder_id === f.id} onClick={() => moveDoc(d, f.id)}>
                <Folder className="h-4 w-4 mr-2 shrink-0" />
                <span className="truncate">{"\u00A0\u00A0".repeat(f.depth)}{f.depth > 0 ? "↳ " : ""}{f.name}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            {onArchive && <DropdownMenuItem data-testid={`file-archive-${d.id}`} onClick={() => onArchive(d)}><Archive className="h-4 w-4 mr-2" /> Archive</DropdownMenuItem>}
            {onMove && <DropdownMenuItem data-testid={`file-move-community-${d.id}`} onClick={() => onMove(d)}><ArrowRightLeft className="h-4 w-4 mr-2" /> Move to another {V.lc("org")}</DropdownMenuItem>}
            <DropdownMenuItem data-testid={`file-delete-${d.id}`} className="text-danger-600 focus:text-danger-600" onClick={() => removeDoc?.(d.id)}><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const statusLine = (d, compact = false) => {
    if (d.status === "processing") return <span className={`${compact ? "" : "block "}text-[11px] text-amber-600 flex items-center gap-1`}><Loader2 className="h-3 w-3 animate-spin" /> Reading &amp; indexing…{compact ? "" : " large files can take a minute"}</span>;
    if (d.status === "queued") return <span className={`${compact ? "" : "block "}text-[11px] text-amber-600`}>Waiting in line{d.queue_position ? ` (#${d.queue_position})` : ""}{compact ? "…" : " — documents process one at a time"}</span>;
    if (d.status === "stored") return (
      <span className="flex items-center gap-2 mt-0.5 flex-wrap">
        <span data-testid={`file-stored-${d.id}`} className="text-[11px] text-amber-700 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Saved — not AI-readable. {d.text_note || "No readable text found."} Download &amp; share still work.</span>
        {!readOnly && <span role="button" tabIndex={0} data-testid={`file-retry-inline-${d.id}`} onClick={(e) => { e.stopPropagation(); reprocess(d); }}
          className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold px-2 py-0.5 hover:bg-amber-200 transition-colors cursor-pointer"><RotateCcw className="h-3 w-3" /> Retry</span>}
      </span>
    );
    if (d.status === "failed") return (
      <span className="flex items-center gap-2 mt-0.5">
        <span className="text-[11px] text-danger-600 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> {d.error || "Couldn't process this file"}</span>
        {!readOnly && <span role="button" tabIndex={0} data-testid={`file-retry-inline-${d.id}`} onClick={(e) => { e.stopPropagation(); reprocess(d); }}
          className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold px-2 py-0.5 hover:bg-amber-200 transition-colors cursor-pointer"><RotateCcw className="h-3 w-3" /> Retry</span>}
      </span>
    );
    return null;
  };

  const renderFolderTree = (parentId, depth) => (childrenOf[parentId] || []).map((f) => {
    const kids = childrenOf[f.id] || [];
    const isCollapsed = !!collapsed[f.id];
    return (
      <div key={f.id}>
        <FolderRow label={f.name} icon={Folder} count={f.doc_count} activeState={active === f.id}
          onClick={() => setActive(f.id)} testid={`folder-${f.id}`} locked={!!f.board_only}
          isPublic={!!f.is_public} depth={depth}
          hasKids={kids.length > 0} expanded={!isCollapsed}
          onToggleExpand={() => setCollapsed((c) => ({ ...c, [f.id]: !c[f.id] }))}
          dnd={{ over: dropTarget === f.id,
                 onOver: () => setDropTarget(f.id), onLeave: () => setDropTarget(null),
                 onDrop: () => { const d = (docs || []).find((x) => x.id === dragId); if (d && d.folder_id !== f.id) moveDoc(d, f.id); setDropTarget(null); },
                 allow: !readOnly }} />
        {kids.length > 0 && !isCollapsed && renderFolderTree(f.id, depth + 1)}
      </div>
    );
  });

  return (
    <section data-testid="documents-library" className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-xl font-extrabold tracking-tight flex items-center gap-2">
            <Files className="h-5 w-5 text-redline-500" strokeWidth={1.75} /> Documents
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">Organize files into folders (nest them, share them, publish them to your {V.lc("org")} website) — your AI reads every one to answer {V.lcs("person")}.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="toggle-folders-btn" onClick={toggleFolders} aria-pressed={foldersOpen}
            title={foldersOpen ? "Hide folders" : "Show folders"}
            className={`inline-flex items-center gap-2 rounded-full border font-semibold text-sm px-4 py-2 transition-colors ${foldersOpen
              ? "border-redline-200 bg-redline-50 text-redline-700 hover:bg-redline-100"
              : "border-ink bg-ink text-white hover:bg-ink-700"}`}>
            {foldersOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}
            <span>{foldersOpen ? "Hide folders" : "Show folders"}</span>
          </button>
          {!readOnly && (<>
            {failedDocs.length > 0 && (
              <button data-testid="retry-all-btn" onClick={reprocessAll} disabled={retryingAll}
                className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 text-amber-800 font-semibold text-sm px-4 py-2 hover:bg-amber-100 disabled:opacity-50 transition-colors">
                {retryingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Retry all interrupted ({failedDocs.length})
              </button>
            )}
            <button data-testid="new-folder-btn" onClick={() => openNewFolder(activeFolderId)}
              className="inline-flex items-center gap-2 rounded-full border border-sand-200 bg-white text-ink font-semibold text-sm px-4 py-2 hover:border-ink transition-colors">
              <FolderPlus className="h-4 w-4" /> New folder
            </button>
            <button data-testid="library-upload-btn" onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-2 rounded-full bg-ink text-white font-semibold text-sm px-4 py-2 hover:bg-ink-700 transition-colors">
              <FilePlus className="h-4 w-4" /> New file
            </button>
            <input ref={fileRef} type="file" multiple hidden accept=".pdf,.txt,.md,.csv,.docx,.xls,.xlsx,.pptx,.zip,.png,.jpg,.jpeg,.webp,.gif,.bmp,.tif,.tiff"
              onChange={(e) => { uploadFiles(e.target.files, activeFolderId); e.target.value = ""; }} />
          </>)}
        </div>
      </div>

      {/* Explorer: nested folder sidebar + files. Drag a file onto a folder to move it. */}
      <div className="flex flex-col lg:flex-row gap-5 items-start">
        <aside data-testid="folder-sidebar"
          className={`w-full overflow-hidden transition-[width,opacity,margin] duration-300 ease-out lg:sticky lg:top-4 self-start ${foldersOpen ? "lg:w-[264px] opacity-100" : "hidden lg:block lg:w-0 lg:opacity-0 lg:-ml-5 lg:pointer-events-none"}`}>
          <div className="rounded-2xl border border-sand-200 bg-white p-2.5 w-full lg:w-[264px]">
          {folders.length > 8 && (
            <div className="relative mb-2 px-1">
              <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="folder-search" value={folderQuery} onChange={(e) => setFolderQuery(e.target.value)} placeholder="Find a folder…"
                className="w-full rounded-lg bg-sand-50 border border-sand-200 pl-8 pr-3 py-1.5 text-xs outline-none focus:border-ink" />
            </div>
          )}
          <FolderRow label="All documents" icon={Files} count={(docs || []).length} activeState={active === "all"}
            onClick={() => setActive("all")} testid="folder-all"
            dnd={{ over: dropTarget === "all", on: () => {}, allow: false }} />
          <div className="my-1.5 border-t border-sand-100" />
          <div className="max-h-[420px] overflow-y-auto pr-0.5 space-y-0.5">
            {folderQuery.trim()
              ? flatFolders.filter((f) => f.name.toLowerCase().includes(folderQuery.toLowerCase())).map((f) => (
                <FolderRow key={f.id} label={f.name} icon={Folder} count={f.doc_count} activeState={active === f.id}
                  onClick={() => setActive(f.id)} testid={`folder-${f.id}`} locked={!!f.board_only} isPublic={!!f.is_public}
                  dnd={{ over: dropTarget === f.id,
                         onOver: () => setDropTarget(f.id), onLeave: () => setDropTarget(null),
                         onDrop: () => { const d = (docs || []).find((x) => x.id === dragId); if (d && d.folder_id !== f.id) moveDoc(d, f.id); setDropTarget(null); },
                         allow: !readOnly }} />
              ))
              : renderFolderTree(null, 0)}
            {folders.length === 0 && <div className="px-3 py-4 text-xs text-muted-foreground text-center">No folders yet.{!readOnly && " Tap “New folder”."}</div>}
          </div>
          <div className="my-1.5 border-t border-sand-100" />
          <FolderRow label="Unfiled" icon={FolderOpen} count={unfiled.doc_count} activeState={active === "unfiled"}
            onClick={() => setActive("unfiled")} testid="folder-unfiled"
            dnd={{ over: dropTarget === "unfiled",
                   onOver: () => setDropTarget("unfiled"), onLeave: () => setDropTarget(null),
                   onDrop: () => { const d = (docs || []).find((x) => x.id === dragId); if (d && d.folder_id) moveDoc(d, ""); setDropTarget(null); },
                   allow: !readOnly }} />
          </div>
        </aside>

        <div className="min-w-0 flex-1 space-y-4">
          {/* Active-folder toolbar */}
          {activeFolderObj && (
            <div className="flex items-center gap-2 flex-wrap text-sm bg-sand-50 border border-sand-200 rounded-xl px-4 py-2.5">
              <span className="font-semibold text-ink flex items-center gap-1.5">
                <Folder className="h-4 w-4 text-redline-500" />
                {activeFolderObj.parent_id && folderName_(activeFolderObj.parent_id) && (
                  <>
                    <button data-testid="folder-parent-crumb" onClick={() => setActive(activeFolderObj.parent_id)} className="text-ink/50 hover:text-ink transition-colors">{folderName_(activeFolderObj.parent_id)}</button>
                    <ChevronRight className="h-3.5 w-3.5 text-ink/30" />
                  </>
                )}
                {activeFolderObj.name}
              </span>
              {activeFolderObj.created_by_name && <span className="text-xs text-muted-foreground">· created by {activeFolderObj.created_by_name}</span>}
              {!readOnly && (
                <>
                  <button data-testid="folder-visibility-btn" onClick={() => toggleVisibility(activeFolderObj)}
                    disabled={!!activeFolderObj.board_inherited}
                    title={activeFolderObj.board_inherited
                      ? `This folder sits inside a ${V.grp()}-only folder, so it's confidential too. Change it on the parent folder.`
                      : activeFolderObj.visibility === "board"
                        ? `Only ${V.lcs("board")} & managers can see this folder. Click to make it visible to ${V.lcs("person")}.`
                        : `${V.many("person")} can see this folder in their portal. Click to restrict it to ${V.grp()} & managers.`}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors disabled:cursor-not-allowed ${
                      activeFolderObj.board_only
                        ? "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100"
                        : "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"}`}>
                    {activeFolderObj.board_only
                      ? <><Lock className="h-3.5 w-3.5" /> {V.Grp()} & managers only{activeFolderObj.board_inherited ? " (from parent)" : ""}</>
                      : <><Users className="h-3.5 w-3.5" /> {V.many("person")} can view</>}
                  </button>
                  <button data-testid="folder-public-btn" onClick={() => togglePublic(activeFolderObj)}
                    disabled={!!activeFolderObj.board_only}
                    title={activeFolderObj.board_only
                      ? `${V.Grp()}-only folders can't go on the public website.`
                      : activeFolderObj.is_public
                        ? `This folder's files show on your public ${V.lc("org")} website. Click to unpublish.`
                        : `Publish this folder's files on your public ${V.lc("org")} website.`}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors disabled:opacity-40 ${
                      activeFolderObj.is_public
                        ? "bg-sky-50 border-sky-300 text-sky-800 hover:bg-sky-100"
                        : "bg-white border-sand-200 text-ink/60 hover:border-ink hover:text-ink"}`}>
                    <Globe className="h-3.5 w-3.5" /> {activeFolderObj.is_public ? `On ${V.lc("org")} website` : "Not on website"}
                  </button>
                </>
              )}
              <div className="ml-auto flex items-center gap-1 flex-wrap">
                <button data-testid="folder-download-btn" onClick={() => downloadFolderZip(activeFolderObj)} disabled={zipBusy}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-ink-600 hover:bg-white transition-colors disabled:opacity-50">
                  {zipBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FolderDown className="h-3.5 w-3.5" />} Download ZIP
                </button>
                {!readOnly && (
                  <>
                    <button data-testid="folder-subfolder-btn" onClick={() => openNewFolder(activeFolderObj.id)}
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-ink-600 hover:bg-white transition-colors"><FolderPlus className="h-3.5 w-3.5" /> Subfolder</button>
                    <button data-testid="folder-rename-btn" onClick={() => { setRenameFor(activeFolderObj); setFolderName(activeFolderObj.name); }}
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-ink-600 hover:bg-white transition-colors"><Pencil className="h-3.5 w-3.5" /> Rename</button>
                    <button data-testid="folder-share-btn" onClick={() => setShareTarget({ kind: "folder", id: activeFolderObj.id, label: activeFolderObj.name })}
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-ink-600 hover:bg-white transition-colors"><Share2 className="h-3.5 w-3.5" /> Share folder</button>
                    <button data-testid="folder-delete-btn" onClick={() => deleteFolder(activeFolderObj)}
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-danger-600 hover:bg-white transition-colors"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="library-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name or description…"
                className="rounded-full bg-white border border-sand-200 pl-9 pr-9 py-2 text-sm outline-none focus:border-ink w-56" />
              {query && (
                <button data-testid="library-search-clear" onClick={() => setQuery("")} aria-label="Clear search"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink/40 hover:text-ink transition-colors">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <select data-testid="library-type-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
              className="rounded-full bg-white border border-sand-200 px-3 py-2 text-sm font-medium outline-none focus:border-ink cursor-pointer">
              <option value="all">All types</option>
              {availableTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select data-testid="library-modified-filter" value={modified} onChange={(e) => setModified(e.target.value)}
              className="rounded-full bg-white border border-sand-200 px-3 py-2 text-sm font-medium outline-none focus:border-ink cursor-pointer">
              <option value="any">Any time</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="year">This year</option>
              <option value="lastyear">Last year</option>
            </select>
            <div className="relative flex items-center">
              <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground absolute left-3 pointer-events-none" />
              <select data-testid="library-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                className="rounded-full bg-white border border-sand-200 pl-8 pr-3 py-2 text-sm font-medium outline-none focus:border-ink appearance-none cursor-pointer">
                <option value="recent">Recently modified</option>
                <option value="name">Name (A–Z)</option>
                <option value="type">Type</option>
              </select>
            </div>
            <div className="ml-auto flex items-center gap-0.5 rounded-full bg-sand-100 p-0.5">
              <button data-testid="library-view-list" onClick={() => setView("list")} title="List view"
                className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${view === "list" ? "bg-white shadow-sm text-ink" : "text-ink-400 hover:text-ink"}`}><List className="h-4 w-4" /></button>
              <button data-testid="library-view-grid" onClick={() => setView("grid")} title="Grid view"
                className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${view === "grid" ? "bg-white shadow-sm text-ink" : "text-ink-400 hover:text-ink"}`}><LayoutGrid className="h-4 w-4" /></button>
            </div>
          </div>

          {/* File list */}
          {scoped.length === 0 ? (
            <div data-testid="library-empty" role={readOnly ? undefined : "button"}
              onClick={() => !readOnly && fileRef.current?.click()}
              className={`bg-white rounded-2xl border p-12 text-center flex flex-col items-center gap-2 transition-colors ${readOnly ? "border-sand-200" : "border-dashed border-sand-300 cursor-pointer hover:border-redline-300 hover:bg-redline-50/30"}`}>
              <div className="h-12 w-12 rounded-2xl bg-redline-50 flex items-center justify-center"><FileText className="h-6 w-6 text-redline-400" strokeWidth={1.5} /></div>
              <div className="font-heading font-extrabold">No documents here yet</div>
              <p className="text-sm text-muted-foreground max-w-xs">{active === "all" ? "Add files with “New file” — or drop a .zip to import whole folders at once." : "Drag a document onto this folder, or use “New file” (uploads land right here)."}</p>
              {!readOnly && (
                <span className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-redline-500 text-white text-xs font-bold px-4 py-2">
                  Click to upload one or more files
                </span>
              )}
            </div>
          ) : view === "grid" ? (
            <div data-testid="library-file-grid" className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {pagedDocs.map((d) => (
                <div key={d.id} data-testid={`file-card-${d.id}`} draggable={!readOnly}
                  onDragStart={() => setDragId(d.id)} onDragEnd={() => { setDragId(null); setDropTarget(null); }}
                  className={`relative bg-white rounded-2xl border border-sand-200 p-4 hover:border-ink/30 hover:shadow-sm transition-all ${dragId === d.id ? "opacity-40" : ""} ${!readOnly ? "cursor-grab active:cursor-grabbing" : ""}`}>
                  <div className="absolute top-2.5 right-2.5">{renderMenu(d)}</div>
                  <div role="button" tabIndex={0} onClick={() => onPreview?.(d)} className="w-full text-left cursor-pointer">
                    <span className="h-11 w-11 rounded-xl bg-redline-50 flex items-center justify-center mb-3"><FileText className="h-5.5 w-5.5 text-redline-500" strokeWidth={1.5} /></span>
                    <div className="text-sm font-semibold text-ink truncate pr-6">{d.title}</div>
                    <div className="text-xs text-muted-foreground truncate mt-0.5">{d.description || d.filename || TYPE_OF(d)}</div>
                    <div className="flex items-center gap-1.5 flex-wrap"><AudienceBadge d={d} labels={audienceLabels} /><ExpiryBadge item={d} compact /></div>
                    <TagChips d={d} readOnly={readOnly} onSave={saveTagsInline} max={3} />
                    <div className="flex items-center gap-2 mt-3 text-[11px] text-muted-foreground">
                      <span className="rounded-full bg-sand-100 px-2 py-0.5 font-medium text-ink-600">{TYPE_OF(d)}</span>
                      <span>{fmtWhen(d.updated_at || d.created_at)}</span>
                    </div>
                    <div className="mt-2">{statusLine(d, true)}</div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-sand-100"><LinkShareCheck d={d} readOnly={readOnly} onToggle={setDocShareByLink} /></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden" data-testid="library-file-list">
              <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_120px_110px_140px_160px_40px] gap-3 px-5 py-2.5 border-b border-sand-100 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                <div>Name</div><div>Type</div><div title="Can residents be texted a download link?">Text link</div><div>Modified</div><div>Added by</div><div></div>
              </div>
              <div className="divide-y divide-sand-100">
                {pagedDocs.map((d) => (
                  <div key={d.id} data-testid={`file-row-${d.id}`}
                    draggable={!readOnly}
                    onDragStart={() => setDragId(d.id)} onDragEnd={() => { setDragId(null); setDropTarget(null); }}
                    className={`group grid grid-cols-[minmax(0,1fr)_auto_auto] sm:grid-cols-[minmax(0,1fr)_120px_110px_140px_160px_40px] gap-3 px-5 py-3 items-center hover:bg-sand-50/60 transition-colors ${dragId === d.id ? "opacity-40" : ""} ${!readOnly ? "cursor-grab active:cursor-grabbing" : ""}`}>
                    <div role="button" tabIndex={0} onClick={() => onPreview?.(d)} className="min-w-0 flex items-center gap-3 text-left cursor-pointer">
                      {!readOnly && <GripVertical className="h-4 w-4 text-sand-300 group-hover:text-sand-400 shrink-0 hidden sm:block" />}
                      <span className="h-9 w-9 rounded-lg bg-redline-50 flex items-center justify-center shrink-0"><FileText className="h-4.5 w-4.5 text-redline-500" strokeWidth={1.5} /></span>
                      <span className="min-w-0">
                        <span className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-ink truncate">{d.title}</span>
                          <AudienceBadge d={d} compact labels={audienceLabels} />
                          <ExpiryBadge item={d} compact />
                        </span>
                        {d.description ? <span className="block text-xs text-muted-foreground truncate">{d.description}</span>
                          : d.filename ? <span className="block text-xs text-muted-foreground truncate">{d.filename}</span> : null}
                        <TagChips d={d} readOnly={readOnly} onSave={saveTagsInline} max={3} />
                        {statusLine(d)}
                      </span>
                    </div>
                    <div className="hidden sm:block text-xs">
                      <span className="rounded-full bg-sand-100 px-2 py-0.5 font-medium text-ink-600">{TYPE_OF(d)}</span>
                    </div>
                    <div className="flex items-center"><LinkShareCheck d={d} readOnly={readOnly} onToggle={setDocShareByLink} /></div>
                    <div className="hidden sm:block text-xs text-muted-foreground">{fmtWhen(d.updated_at || d.created_at)}</div>
                    <div className="hidden sm:block text-xs text-muted-foreground truncate">{d.created_by_name || "—"}</div>
                    <div className="flex items-center justify-end">{renderMenu(d)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <ShowMore remaining={pagedRemaining} onClick={showMoreDocs} label="Show more files" testid="library-show-more" />
        </div>
      </div>

      {/* New folder dialog — name, destination (nesting) + public-website toggle */}
      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><FolderPlus className="h-5 w-5 text-redline-500" /> New folder</DialogTitle>
            <DialogDescription>Group related documents — e.g. Governing Docs, Budgets, Meeting Minutes. Folders can live inside other folders.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold">Folder name</label>
              <input data-testid="new-folder-input" autoFocus value={folderForm.name}
                onChange={(e) => setFolderForm((f) => ({ ...f, name: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && createFolder()}
                placeholder="Folder name" className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
            </div>
            <div>
              <label className="text-sm font-semibold">Destination folder</label>
              <select data-testid="new-folder-parent" value={folderForm.parent_id}
                onChange={(e) => setFolderForm((f) => ({ ...f, parent_id: e.target.value }))}
                className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 cursor-pointer">
                <option value="">Top level (no parent)</option>
                {flatFolders.map((f) => (
                  <option key={f.id} value={f.id}>{"\u00A0\u00A0\u00A0".repeat(f.depth)}{f.depth > 0 ? "↳ " : ""}{f.name}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">Pick a parent to nest this folder inside it.</p>
            </div>
            <div>
              <label className="text-sm font-semibold">Who can see it</label>
              <select data-testid="new-folder-visibility" value={folderForm.visibility}
                onChange={(e) => setFolderForm((f) => ({ ...f, visibility: e.target.value, is_public: e.target.value === "board" ? false : f.is_public }))}
                className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 cursor-pointer">
                <option value="residents">{V.many("person")} can view</option>
                <option value="board">{V.Grp()} &amp; managers only</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                {folderForm.visibility === "board"
                  ? `Confidential — the concierge never shares these files with ${V.lcs("person")}, and they stay out of the portal.`
                  : `Files here can be shared with ${V.lcs("person")} and answered on by the concierge.`}
                {parentIsLocked && ` The folder you're nesting this inside is already ${V.grp()}-only, so everything in it stays confidential either way.`}
              </p>
            </div>
            <div className="flex items-start justify-between gap-4 rounded-xl border border-sand-200 bg-sand-50 px-3.5 py-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold flex items-center gap-1.5"><Globe className="h-4 w-4 text-sky-600" /> Public website folder</div>
                <p className="text-xs text-muted-foreground mt-0.5">Files in this folder are published on your {V.lc("org")}'s public website — visitors can download them without signing in.</p>
              </div>
              <Switch data-testid="new-folder-public" checked={folderForm.is_public}
                disabled={folderForm.visibility === "board" || parentIsLocked}
                onCheckedChange={(v) => setFolderForm((f) => ({ ...f, is_public: v }))} />
            </div>
            <button data-testid="new-folder-save" onClick={createFolder} disabled={!folderForm.name.trim()}
              className="w-full rounded-full bg-redline-500 text-white font-semibold py-2.5 hover:bg-redline-600 disabled:opacity-50 transition-colors">Create folder</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rename folder dialog */}
      <Dialog open={!!renameFor} onOpenChange={(o) => !o && setRenameFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Pencil className="h-5 w-5 text-redline-500" /> Rename folder</DialogTitle>
            <DialogDescription className="sr-only">Rename this folder.</DialogDescription>
          </DialogHeader>
          <input data-testid="rename-folder-input" autoFocus value={folderName} onChange={(e) => setFolderName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doRename()}
            className="w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
          <button data-testid="rename-folder-save" onClick={doRename} disabled={!folderName.trim()}
            className="w-full rounded-full bg-ink text-white font-semibold py-2.5 hover:bg-ink-700 disabled:opacity-50 transition-colors">Save</button>
        </DialogContent>
      </Dialog>

      {/* Rename file dialog */}
      <Dialog open={!!renameDocFor} onOpenChange={(o) => !o && setRenameDocFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Pencil className="h-5 w-5 text-redline-500" /> Rename file</DialogTitle>
            <DialogDescription>Renames the document&apos;s display title everywhere{renameDocFor?.filename ? ` (the original file "${renameDocFor.filename}" keeps its name on download)` : ""}.</DialogDescription>
          </DialogHeader>
          <input data-testid="rename-doc-input" autoFocus value={docTitle} onChange={(e) => setDocTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doRenameDoc()}
            className="w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
          <button data-testid="rename-doc-save" onClick={doRenameDoc} disabled={!docTitle.trim()}
            className="w-full rounded-full bg-ink text-white font-semibold py-2.5 hover:bg-ink-700 disabled:opacity-50 transition-colors">Save</button>
        </DialogContent>
      </Dialog>

      {/* Aliases / tags dialog — the names residents actually use for a file */}
      <Dialog open={!!tagsFor} onOpenChange={(o) => !o && setTagsFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Tag className="h-5 w-5 text-redline-500" /> Aliases &amp; tags</DialogTitle>
            <DialogDescription>
              {V.many("person")} rarely ask for a file by its exact name. Add the names they actually use — e.g. tag
              {" "}<span className="font-semibold text-ink">“{tagsFor?.title || "this document"}”</span> with
              {" "}<span className="font-semibold text-ink">CC&amp;Rs</span> — and the concierge will find and send it when asked either way.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-1.5 min-h-[34px] rounded-xl border border-sand-200 px-3 py-2">
            {tagsDraft.map((t) => (
              <span key={t} data-testid={`tag-chip-${t}`} className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 bg-sky-50 border border-sky-100 rounded-full pl-2 pr-1 py-0.5">
                {t}
                <button data-testid={`tag-remove-${t}`} onClick={() => setTagsDraft((cur) => cur.filter((x) => x !== t))}
                  className="h-4 w-4 rounded-full flex items-center justify-center hover:bg-sky-100"><X className="h-3 w-3" /></button>
              </span>
            ))}
            <input data-testid="tag-input" autoFocus value={tagInput} onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); }
                else if (e.key === "Backspace" && !tagInput) setTagsDraft((cur) => cur.slice(0, -1));
              }}
              onBlur={() => tagInput.trim() && addTag()}
              placeholder={tagsDraft.length ? "Add another…" : "e.g. CC&Rs, Covenants, Declaration"}
              className="flex-1 min-w-[120px] text-sm outline-none py-0.5" />
          </div>
          <p className="text-[11px] text-muted-foreground -mt-1">Press Enter or comma after each alias · up to 10</p>
          <button data-testid="tags-save" onClick={doSaveTags}
            className="w-full rounded-full bg-ink text-white font-semibold py-2.5 hover:bg-ink-700 transition-colors">Save aliases</button>
        </DialogContent>
      </Dialog>

      <ShareDialog tenantId={tenantId} target={shareTarget} onClose={() => setShareTarget(null)} />
    </section>
  );
}

function FolderRow({ label, icon: Icon, count, activeState, onClick, testid, dnd = {}, locked = false, isPublic = false,
                     depth = 0, hasKids = false, expanded = true, onToggleExpand }) {
  const canDrop = dnd.allow && dnd.onDrop;
  return (
    <button data-testid={testid} onClick={onClick}
      onDragOver={canDrop ? (e) => { e.preventDefault(); dnd.onOver?.(); } : undefined}
      onDragLeave={canDrop ? () => dnd.onLeave?.() : undefined}
      onDrop={canDrop ? (e) => { e.preventDefault(); dnd.onDrop(); } : undefined}
      style={depth ? { paddingLeft: `${12 + depth * 16}px` } : undefined}
      className={`w-full flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-left transition-colors
        ${activeState ? "bg-ink text-white" : "text-ink-700 hover:bg-sand-50"}
        ${dnd.over ? "ring-2 ring-redline-400 ring-offset-1 bg-redline-50" : ""}`}>
      {hasKids ? (
        <span role="button" tabIndex={0} data-testid={`${testid}-toggle`}
          onClick={(e) => { e.stopPropagation(); onToggleExpand?.(); }}
          className={`h-4 w-4 shrink-0 -ml-1 flex items-center justify-center rounded hover:bg-white/20 ${activeState ? "text-white/80" : "text-ink/40"}`}>
          <ChevronRight className={`h-3.5 w-3.5 transition-transform duration-150 ${expanded ? "rotate-90" : ""}`} />
        </span>
      ) : depth > 0 ? <span className="w-3 shrink-0" /> : null}
      <Icon className={`h-4 w-4 shrink-0 ${activeState ? "text-white" : "text-redline-500"}`} strokeWidth={1.75} />
      <span className="truncate flex-1">{label}</span>
      {isPublic && <Globe data-testid={`${testid}-public`} className={`h-3 w-3 shrink-0 ${activeState ? "text-white/70" : "text-sky-600"}`} strokeWidth={2} title={`On the ${V.lc("org")} website`} />}
      {locked && <Lock data-testid={`${testid}-lock`} className={`h-3 w-3 shrink-0 ${activeState ? "text-white/70" : "text-amber-600"}`} strokeWidth={2} title={`${V.Grp()} & managers only`} />}
      <span className={`text-[11px] rounded-full px-1.5 py-0.5 shrink-0 ${activeState ? "bg-white/20" : "bg-sand-100 text-ink-500"}`}>{count}</span>
    </button>
  );
}

function ShareDialog({ tenantId, target, onClose }) {
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLink(null); setCopied(false);
    if (!target) return;
    setLoading(true);
    api.post(`/tenants/${tenantId}/shares`, { kind: target.kind, target_id: target.id })
      .then((r) => setLink(r.data))
      .catch(() => toast.error("Could not create share link"))
      .finally(() => setLoading(false));
  }, [target, tenantId]);

  const url = link ? `${window.location.origin}/share/${link.token}` : "";
  const copy = () => { navigator.clipboard.writeText(url); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 1800); };
  const revoke = async () => {
    if (!link) return;
    try { await api.delete(`/tenants/${tenantId}/shares/${link.id}`); toast.success("Link revoked"); onClose(); }
    catch { toast.error("Could not revoke"); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Share2 className="h-5 w-5 text-redline-500" /> Share {target?.kind}</DialogTitle>
          <DialogDescription>Anyone you send this to can open <span className="font-semibold text-ink">{target?.label}</span> — but only after signing in to Ask Redline with access to this {V.lc("org")}. It's never public. Revoke it anytime.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-redline-500" /></div>
        ) : (
          <>
            <div className="flex items-center gap-2 rounded-xl border border-sand-200 bg-sand-50 px-3 py-2.5">
              <Link2 className="h-4 w-4 text-muted-foreground shrink-0" />
              <input data-testid="share-link-input" readOnly value={url} className="flex-1 bg-transparent text-sm outline-none text-ink-700 min-w-0" />
              <button data-testid="share-copy-btn" onClick={copy} className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-ink text-white text-xs font-semibold px-3 py-1.5 hover:bg-ink-700 transition-colors">
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <button data-testid="share-revoke-btn" onClick={revoke} className="inline-flex items-center gap-1.5 text-sm font-semibold text-redline-600 hover:text-redline-700 transition-colors">
              <X className="h-4 w-4" /> Revoke this link
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
